import { useId, type KeyboardEvent, type ReactNode } from "react";
import { cn } from "../helpers/class-names";

export type TabOption<T extends string> = {
  value: T;
  label: ReactNode;
  content: ReactNode;
};

export function Tabs<T extends string>({
  className,
  label,
  options,
  value,
  onValueChange,
}: {
  className?: string;
  label: string;
  options: readonly TabOption<T>[];
  value: T;
  onValueChange: (value: T) => void;
}) {
  const id = useId();
  const selected = options.find((option) => option.value === value) ?? options[0];

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    let next: number;
    switch (event.key) {
      case "ArrowRight":
        next = (index + 1) % options.length;
        break;
      case "ArrowLeft":
        next = (index - 1 + options.length) % options.length;
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = options.length - 1;
        break;
      default:
        return;
    }
    const option = options[next];
    if (!option) {
      return;
    }
    event.preventDefault();
    onValueChange(option.value);
    const buttons = event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>("[role=tab]");
    buttons?.[next]?.focus();
  }

  return (
    <div className={cn("ui-tabs", className)}>
      <div aria-label={label} className="ui-tab-list" role="tablist">
        {options.map((option, index) => (
          <button
            aria-controls={`${id}-panel-${option.value}`}
            aria-selected={option.value === selected?.value}
            className="ui-tab"
            id={`${id}-tab-${option.value}`}
            key={option.value}
            onClick={() => onValueChange(option.value)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            role="tab"
            tabIndex={option.value === selected?.value ? 0 : -1}
            type="button"
          >
            {option.label}
          </button>
        ))}
      </div>
      {options.map((option) => (
        <div
          aria-labelledby={`${id}-tab-${option.value}`}
          className="ui-tab-panel"
          hidden={option.value !== selected?.value}
          id={`${id}-panel-${option.value}`}
          key={option.value}
          role="tabpanel"
          tabIndex={0}
        >
          {option.value === selected?.value && option.content}
        </div>
      ))}
    </div>
  );
}
